 import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import GoogleSignInButton from '../components/GoogleSignInButton';
import OTPInput from '../components/OTPInput';
import Turnstile from '../components/Turnstile';
import { Mail, Lock, User, Building2, Eye, EyeOff, Phone, MapPin, ArrowRight, ArrowLeft, AlertCircle, Gauge, Leaf, Ticket, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../lib/api/client';
import { COUNTRIES } from '../constants/countries';
import '../styles/auth.css';
import '../styles/otp.css';

// Mirrors Turnstile.js's own check: when no site key is configured, that
// component immediately reports an empty token and never renders a widget
// at all (the backend fails OPEN in that case — see app/core/turnstile.py).
// Gating a button on a turnstile token must not apply in that scenario, or
// it would permanently disable the button instead of preserving fail-open.
const TURNSTILE_CONFIGURED = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

const COUNTRY_CODES = [
  { value: '+91', label: '+91 India' },
  { value: '+1', label: '+1 USA' },
  { value: '+44', label: '+44 UK' },
  { value: '+61', label: '+61 Australia' },
  { value: '+86', label: '+86 China' },
  { value: '+81', label: '+81 Japan' },
  { value: '+82', label: '+82 South Korea' },
  { value: '+65', label: '+65 Singapore' },
  { value: '+60', label: '+60 Malaysia' },
  { value: '+63', label: '+63 Philippines' },
  { value: '+62', label: '+62 Indonesia' },
  { value: '+66', label: '+66 Thailand' },
  { value: '+84', label: '+84 Vietnam' },
  { value: '+971', label: '+971 UAE' },
  { value: '+966', label: '+966 Saudi Arabia' },
  { value: '+974', label: '+974 Qatar' },
  { value: '+973', label: '+973 Bahrain' },
  { value: '+968', label: '+968 Oman' },
  { value: '+965', label: '+965 Kuwait' },
  { value: '+962', label: '+962 Jordan' },
  { value: '+961', label: '+961 Lebanon' },
  { value: '+972', label: '+972 Israel' },
  { value: '+90', label: '+90 Turkey' },
  { value: '+7', label: '+7 Russia' },
  { value: '+49', label: '+49 Germany' },
  { value: '+33', label: '+33 France' },
  { value: '+34', label: '+34 Spain' },
  { value: '+39', label: '+39 Italy' },
  { value: '+31', label: '+31 Netherlands' },
  { value: '+32', label: '+32 Belgium' },
  { value: '+41', label: '+41 Switzerland' },
  { value: '+43', label: '+43 Austria' },
  { value: '+46', label: '+46 Sweden' },
  { value: '+47', label: '+47 Norway' },
  { value: '+45', label: '+45 Denmark' },
  { value: '+48', label: '+48 Poland' },
  { value: '+351', label: '+351 Portugal' },
  { value: '+353', label: '+353 Ireland' },
  { value: '+358', label: '+358 Finland' },
  { value: '+30', label: '+30 Greece' },
  { value: '+36', label: '+36 Hungary' },
  { value: '+40', label: '+40 Romania' },
  { value: '+380', label: '+380 Ukraine' },
  { value: '+420', label: '+420 Czech Republic' },
  { value: '+27', label: '+27 South Africa' },
  { value: '+234', label: '+234 Nigeria' },
  { value: '+254', label: '+254 Kenya' },
  { value: '+233', label: '+233 Ghana' },
  { value: '+256', label: '+256 Uganda' },
  { value: '+255', label: '+255 Tanzania' },
  { value: '+251', label: '+251 Ethiopia' },
  { value: '+20', label: '+20 Egypt' },
  { value: '+212', label: '+212 Morocco' },
  { value: '+213', label: '+213 Algeria' },
  { value: '+216', label: '+216 Tunisia' },
  { value: '+55', label: '+55 Brazil' },
  { value: '+52', label: '+52 Mexico' },
  { value: '+57', label: '+57 Colombia' },
  { value: '+56', label: '+56 Chile' },
  { value: '+54', label: '+54 Argentina' },
  { value: '+51', label: '+51 Peru' },
  { value: '+58', label: '+58 Venezuela' },
  { value: '+64', label: '+64 New Zealand' },
  { value: '+92', label: '+92 Pakistan' },
  { value: '+94', label: '+94 Sri Lanka' },
  { value: '+880', label: '+880 Bangladesh' },
  { value: '+977', label: '+977 Nepal' },
  { value: '+95', label: '+95 Myanmar' },
  { value: '+855', label: '+855 Cambodia' },
  { value: '+856', label: '+856 Laos' },
  { value: '+852', label: '+852 Hong Kong' },
  { value: '+886', label: '+886 Taiwan' },
  { value: '+93', label: '+93 Afghanistan' },
  { value: '+98', label: '+98 Iran' },
  { value: '+964', label: '+964 Iraq' },
  { value: '+960', label: '+960 Maldives' },
  { value: '+230', label: '+230 Mauritius' },
  { value: '+243', label: '+243 DR Congo' },
  { value: '+237', label: '+237 Cameroon' },
  { value: '+225', label: '+225 Ivory Coast' },
  { value: '+221', label: '+221 Senegal' },
  { value: '+263', label: '+263 Zimbabwe' },
  { value: '+260', label: '+260 Zambia' },
  { value: '+244', label: '+244 Angola' },
  { value: '+258', label: '+258 Mozambique' },
];

export default function Signup() {
  const [currentStep, setCurrentStep] = useState(1); // Start at basic info step

  // OTP verification state
  const [otp, setOtp] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  // Helper to ensure error is always a string
  const safeError = (err) => {
    if (typeof err === 'string') return err;
    if (typeof err === 'object') {
      return err.message || err.msg || JSON.stringify(err);
    }
    return String(err);
  };
  const [otpError, setOtpError] = useState('');
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [emailVerified, setEmailVerified] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    countryCode: { value: '+91', label: '+91 India' },
    phoneNumber: '',
    country: null,
    institution: '',
    password: '',
    confirmPassword: '',
    referralCode: ''
  });
  const [referralInfo, setReferralInfo] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralError, setReferralError] = useState('');
  const [hasReferralCode, setHasReferralCode] = useState(null);
  const [searchParams] = useSearchParams();
  // Honour ?redirect= exactly as Login does. Without this, a student sent
  // here from /downloadmars to create an account lands on the generator
  // and has no idea how to get back to their download.
  const redirectTo = searchParams.get('redirect') || '/generator';

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referralCode: refCode.toUpperCase() }));
      setHasReferralCode('yes');
      (async () => {
        try {
          const { lookupFellowshipCode } = await import('../lib/api/fellowship');
          const data = await lookupFellowshipCode(refCode);
          setReferralInfo(data);
        } catch { setReferralError('Invalid referral code'); }
      })();
    }
  }, [searchParams]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');       // step 4: register
  const [turnstileReset, setTurnstileReset] = useState(0);        // re-arm after a failed register
  const [otpTurnstileToken, setOtpTurnstileToken] = useState(''); // step 1: send OTP email

  const navigate = useNavigate();
  const { register } = useAuth();


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({ ...prev, [name]: selectedOption }));
    setError('');
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        // Step 1: Basic Info (Name, Email)
        if (!formData.fullName.trim()) {
          setError('Please enter your full name');
          return false;
        }
        if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        return true;
      case 2:
        // Step 2: OTP Verification (handled separately)
        return emailVerified;
      case 3:
        // Step 3: Location (Country, Institution, Phone)
        if (!formData.country) {
          setError('Please select your country');
          return false;
        }
        if (!formData.institution.trim()) {
          setError('Please enter your institution name');
          return false;
        }
        if (!formData.phoneNumber.trim()) {
          setError('Please enter your phone number');
          return false;
        }
        if (hasReferralCode === null) {
          setError('Please select whether you have a referral code');
          return false;
        }
        if (hasReferralCode === 'yes' && formData.referralCode.trim().length > 12) {
          setError('Referral code must be at most 12 characters');
          return false;
        }
        return true;
      case 4:
        // Step 4: Password/Security
        if (formData.password.length < 8) {
          setError('Password must be at least 8 characters');
          return false;
        }
        if (!/[A-Z]/.test(formData.password)) {
          setError('Password must contain at least one uppercase letter (A-Z)');
          return false;
        }
        if (!/[a-z]/.test(formData.password)) {
          setError('Password must contain at least one lowercase letter (a-z)');
          return false;
        }
        if (!/[0-9]/.test(formData.password)) {
          setError('Password must contain at least one number (0-9)');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        if (!agreed) {
          setError('Please agree to the Terms and Conditions');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const prevStep = () => {
    if (currentStep === 2 && emailVerified) {
      // If going back from email verify step, reset verification
      setEmailVerified(false);
      setOtp('');
      setOtpError('');
    }
    setCurrentStep(prev => prev - 1);
    setError('');
  };

  // Send OTP to email - returns true if successful
  const sendOtp = async () => {
    // Prevent duplicate sends
    if (isSendingOtp) return false;

    setIsSendingOtp(true);
    setError('');
    setOtpError('');
    setOtp(''); // Clear any old OTP

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/send-verification-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email, turnstile_token: otpTurnstileToken })
      });

      const data = await response.json();

      if (response.ok) {
        // DEV MODE: Auto-fill OTP if returned
        if (data.dev_otp) {
          setOtp(data.dev_otp);
          alert(`DEV MODE OTP: ${data.dev_otp}`);
        }

        // Start countdown
        setResendCountdown(60);
        setCanResendOtp(false);
        const timer = setInterval(() => {
          setResendCountdown((prev) => {
            if (prev <= 1) {
              setCanResendOtp(true);
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        return true;
      } else {
        // Show error on step 1 (not OTP error) so user sees it before advancing
        setError(data.detail || 'Failed to send OTP');
        return false;
      }
    } catch (err) {
      setError('Network error. Please try again.');
      return false;
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Verify OTP
  const verifyOtp = async (otpCode) => {
    setIsVerifyingOtp(true);
    setOtpError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          otp: otpCode
        })
      });

      const data = await response.json();

      if (response.ok && data.verified) {
        setEmailVerified(true);
        setOtpError('');
        // Auto-advance to next step after verification
        setTimeout(() => {
          setCurrentStep(3); // Go to location step
        }, 500);
      } else {
        setOtpError(data.detail || data.error || 'Invalid verification code');
        setOtp('');
      }
    } catch (err) {
      setOtpError('Network error. Please try again.');
      setOtp('');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Resend OTP
  const resendOtp = async () => {
    if (!canResendOtp) return;
    await sendOtp();
  };

  const nextStep = async () => {
    if (currentStep === 0) {
      setCurrentStep(1);
    } else if (currentStep === 1 && validateStep(1)) {
      // After basic info, send OTP - only advance if successful
      const success = await sendOtp();
      if (success) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2 && emailVerified) {
      // Email verified, go to location
      setCurrentStep(3);
    } else if (currentStep === 3 && validateStep(3)) {
      // Location validated, go to password/security step
      setCurrentStep(4);
    } else if (currentStep === 3) {
      // If validation fails, show error but don't advance
      console.log('Step 3 validation failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(4)) return;

    setLoading(true);
    setError('');

    try {
      const institutionName = formData.institution.trim();

      await register({
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        country: formData.country?.value,
        phoneNumber: `${formData.countryCode?.value || '+91'}${formData.phoneNumber}`,
        organization: institutionName,
        referralCode: formData.referralCode?.trim() || undefined,
        // validateStep(4) already refuses to submit unless this is true; passing
        // it means the acceptance is recorded server-side instead of only
        // gating the button.
        termsAccepted: agreed,
        turnstileToken,
      });

      // Back to wherever they came from (e.g. /downloadmars), else the dashboard.
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // That attempt consumed the Turnstile token; re-arm for a fresh one so a
      // retry isn't rejected as timeout-or-duplicate.
      setTurnstileToken('');
      setTurnstileReset((n) => n + 1);
      console.error('Signup error:', err);
      console.log('Error type:', typeof err);
      console.log('Error keys:', Object.keys(err));
      console.log('Error.message:', err.message);
      console.log('Error.response:', err.response);
      console.log('Error response data:', err.response?.data);
      console.log('Error response detail:', err.response?.data?.detail);
      console.log('Full error JSON:', JSON.stringify(err, Object.getOwnPropertyNames(err)));

      // Extract error message from various error formats
      let errorMessage = 'Failed to create account. Please try again.';

      // Check if error.message is an array (from our API wrapper)
      if (Array.isArray(err.message)) {
        errorMessage = err.message.map(e => {
          if (typeof e === 'string') return e;
          if (e.msg) return e.msg;
          if (e.message) return e.message;
          return 'Validation error';
        }).join(', ');
      } else if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        console.log('Detail type:', typeof detail, 'Is array:', Array.isArray(detail));

        if (Array.isArray(detail)) {
          // Pydantic validation errors — each item's loc is like
          // ["body", "referral_code"]. Without naming the field, a failure
          // on an earlier step (e.g. referral code) shows a bare message like
          // "String should have at most 12 characters" on whatever step the
          // user happens to be viewing when they hit Create Account (usually
          // the password step) — reads as a password bug when it isn't one.
          const FIELD_LABELS = {
            full_name: 'Full name', email: 'Email', password: 'Password',
            country: 'Country', phone_number: 'Phone number',
            organization: 'Institution', referral_code: 'Referral code',
          };
          errorMessage = detail.map(e => {
            console.log('Detail item:', e, 'Type:', typeof e);
            if (typeof e === 'string') return e;
            const msg = e.msg || e.message || 'Validation error';
            const field = Array.isArray(e.loc) ? e.loc[e.loc.length - 1] : null;
            const label = FIELD_LABELS[field];
            return label ? `${label}: ${msg}` : msg;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          // detail is an object
          errorMessage = JSON.stringify(detail);
        }
      } else if (err.response?.data?.message) {
        const message = err.response.data.message;
        if (Array.isArray(message)) {
          errorMessage = message.map(e => {
            if (typeof e === 'string') return e;
            return e.msg || e.message || String(e);
          }).join(', ');
        } else {
          errorMessage = String(message);
        }
      } else if (typeof err.message === 'string') {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderProgressDots = () => {
    if (currentStep === 0) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
        {[1, 2, 3, 4].map(step => (
          <div
            key={step}
            style={{
              width: currentStep === step ? '32px' : '8px',
              height: '8px',
              borderRadius: '4px',
              backgroundColor: currentStep >= step ? '#f97316' : '#e5e7eb',
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-container">
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px', textAlign: 'center' }}>
              Let's start with the basics
            </h3>

            {/* Signing up with Google never reaches step 4, where the Terms
                checkbox lives — so it needs its own. Same `agreed` state, so a
                candidate who ticks here and then falls back to the manual form
                does not have to tick twice. */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span>
                  I agree to the{' '}
                  <Link to="/policies/terms-and-conditions" className="terms-link">
                    Terms and Conditions
                  </Link>
                </span>
              </label>
            </div>

            <GoogleSignInButton text="Sign up with Google" disabled={!agreed} />

            <div className="auth-divider">
              <span>or enter details manually</span>
            </div>

            <div className="form-group">
              <label><User size={20} /> Full Name</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter your full name"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label><Mail size={20} /> Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
              />
            </div>
            
            <p style={{ marginTop: '16px', fontSize: '14px', color: '#6b7280', textAlign: 'center' }}>
              Already have an account? <Link to="/login" style={{ color: '#f97316', fontWeight: '600' }}>Log in</Link>
            </p>
          </div>
        );

      case 2:
        return (
          <div className="step-container" style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Verify Your Email
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '30px' }}>
              We sent a 6-digit code to <strong>{formData.email}</strong>
            </p>

            <div style={{ marginTop: '30px' }}>
              <OTPInput
                value={otp}
                onChange={setOtp}
                onComplete={verifyOtp}
                error={otpError}
                disabled={isVerifyingOtp}
              />
            </div>

            {otpError && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '20px',
                color: '#991b1b',
                fontSize: '14px'
              }}>
                {otpError}
              </div>
            )}

            <div style={{ marginTop: '30px' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '15px' }}>
                Didn't receive the code?
              </p>

              {canResendOtp ? (
                <button
                  type="button"
                  onClick={resendOtp}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#f97316',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Resend Code
                </button>
              ) : (
                <p style={{ color: '#9ca3af', fontSize: '14px' }}>
                  Resend available in {resendCountdown}s
                </p>
              )}
            </div>

            <div style={{
              marginTop: '30px',
              padding: '15px',
              background: '#fff7ed',
              borderRadius: '8px',
              borderLeft: '4px solid #f97316'
            }}>
              <p style={{ fontSize: '13px', color: '#78350f', margin: 0 }}>
                <strong>⏱️ Code expires in 10 minutes</strong><br />
                Enter the code to continue with your registration.
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-container">
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px', textAlign: 'center' }}>
              Where are you from?
            </h3>

            <div className="form-group">
              <label><MapPin size={20} /> Country</label>
              <Select
                name="country"
                options={COUNTRIES}
                value={formData.country}
                onChange={(opt) => handleSelectChange('country', opt)}
                placeholder="Select your country"
                menuPortalTarget={document.body}
                menuPlacement="auto"
                maxMenuHeight={200}
                styles={{
                  control: (base) => ({ ...base, borderColor: '#e5e7eb', '&:hover': { borderColor: '#f97316' } }),
                  option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? '#f97316' : state.isFocused ? '#fff7ed' : 'white' }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </div>

            <div className="form-group">
              <label><Building2 size={20} /> Institution/Organization</label>
              <input
                type="text"
                name="institution"
                value={formData.institution}
                onChange={handleChange}
                placeholder="e.g. University of Oxford"
              />
            </div>

            <div className="form-group">
              <label><Phone size={20} /> Phone Number</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <CreatableSelect
                  options={COUNTRY_CODES}
                  value={formData.countryCode}
                  onChange={(option) => setFormData(prev => ({ ...prev, countryCode: option }))}
                  placeholder="+91"
                  formatCreateLabel={(input) => `Use: ${input}`}
                  isSearchable={true}
                  styles={{
                    container: (base) => ({ ...base, width: '160px', flexShrink: 0 }),
                    control: (base) => ({ ...base, minHeight: '44px', borderRadius: '8px', borderColor: '#e5e7eb' }),
                    menu: (base) => ({ ...base, zIndex: 20 }),
                    singleValue: (base) => ({ ...base, fontSize: '14px' }),
                    option: (base) => ({ ...base, fontSize: '14px' }),
                  }}
                />
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="123-456-7890"
                  style={{ flex: 1 }}
                />
              </div>
            </div>

            <div style={{ marginTop: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '10px' }}>
                <Ticket size={18} style={{ color: '#6b7280' }} />
                Do you have a referral code?
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setHasReferralCode('yes');
                    setError('');
                  }}
                  style={{
                    flex: 1, padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                    cursor: 'pointer', transition: 'all 0.2s ease',
                    border: hasReferralCode === 'yes' ? '2px solid #1e3a8a' : '2px solid #d1d5db',
                    background: hasReferralCode === 'yes' ? '#1e3a8a' : '#fff',
                    color: hasReferralCode === 'yes' ? '#fff' : '#374151',
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setHasReferralCode('no');
                    setFormData(prev => ({ ...prev, referralCode: '' }));
                    setReferralInfo(null);
                    setReferralError('');
                    setError('');
                  }}
                  style={{
                    flex: 1, padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                    cursor: 'pointer', transition: 'all 0.2s ease',
                    border: hasReferralCode === 'no' ? '2px solid #1e3a8a' : '2px solid #d1d5db',
                    background: hasReferralCode === 'no' ? '#1e3a8a' : '#fff',
                    color: hasReferralCode === 'no' ? '#fff' : '#374151',
                  }}
                >
                  No
                </button>
              </div>

              {hasReferralCode === 'yes' && (
                <div className="form-group" style={{ marginTop: '12px' }}>
                  <input
                    type="text"
                    name="referralCode"
                    value={formData.referralCode}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      setFormData(prev => ({ ...prev, referralCode: val }));
                      setReferralInfo(null);
                      setReferralError('');
                    }}
                    onBlur={async () => {
                      const code = formData.referralCode.trim();
                      if (code.length < 3) return;
                      setReferralLoading(true);
                      setReferralError('');
                      try {
                        const { lookupFellowshipCode } = await import('../lib/api/fellowship');
                        const data = await lookupFellowshipCode(code);
                        setReferralInfo(data);
                      } catch {
                        setReferralError('Invalid referral code');
                        setReferralInfo(null);
                      } finally {
                        setReferralLoading(false);
                      }
                    }}
                    placeholder="e.g. F-MXT23"
                    style={{ textTransform: 'uppercase' }}
                    maxLength={12}
                    autoFocus
                  />
                  {referralLoading && <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>Checking code...</p>}
                  {referralInfo && (
                    <p style={{ color: '#059669', fontSize: '13px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CheckCircle2 size={14} /> Referred by: <strong>{referralInfo.referrer_name}</strong> ({referralInfo.referrer_level})
                    </p>
                  )}
                  {referralError && <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '4px' }}>{referralError}</p>}
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-container">
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px', textAlign: 'center' }}>
              Secure your account
            </h3>

            <div className="form-group">
              <label><Lock size={20} /> Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="At least 8 characters"
                  minLength="8"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              
              {/* Password strength checklist */}
              {formData.password && (
                <div style={{ marginTop: '10px', marginBottom: '10px', fontSize: '13px', color: '#6b7280' }}>
                  <p style={{ marginBottom: '6px', fontWeight: '600', color: '#374151' }}>Password must contain:</p>
                  <ul style={{ paddingLeft: '0', listStyleType: 'none', margin: '0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {[
                      { ok: formData.password.length >= 8, text: 'At least 8 characters' },
                      { ok: /[A-Z]/.test(formData.password), text: 'At least one uppercase letter (A-Z)' },
                      { ok: /[a-z]/.test(formData.password), text: 'At least one lowercase letter (a-z)' },
                      { ok: /[0-9]/.test(formData.password), text: 'At least one number (0-9)' },
                      { ok: /[^A-Za-z0-9]/.test(formData.password), text: 'At least one special character/symbol (e.g. @, #, $, %)' }
                    ].map(({ ok, text }) => (
                      <li key={text} style={{ color: ok ? '#16a34a' : '#9ca3af', display: 'flex', alignItems: 'center', gap: '8px', transition: 'color 0.2s ease' }}>
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          width: '16px', 
                          height: '16px', 
                          borderRadius: '50%', 
                          background: ok ? '#dcfce7' : '#f3f4f6',
                          color: ok ? '#16a34a' : '#9ca3af',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          transition: 'all 0.2s ease'
                        }}>
                          {ok ? '✓' : '○'}
                        </span>
                        {text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {error && (error.includes('Password must') || error.includes('password must') || error.includes('Password complexity') || error.includes('Password is too simple')) && (
                <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <AlertCircle size={14} /> {safeError(error)}
                </p>
              )}

            </div>

            <div className="form-group">
              <label><Lock size={20} /> Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  minLength="8"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {error === 'Passwords do not match' && (
                <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <AlertCircle size={14} /> Passwords do not match
                </p>
              )}

            </div>

            <div className="form-group">
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span>
                  I agree to the{' '}
                  <Link to="/policies/terms-and-conditions" className="terms-link">
                    Terms and Conditions
                  </Link>
                </span>
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="auth-container">
      <Helmet>
        <title>Sign Up | TeachGenie™.ai</title>
        <meta name="description" content="Create a free TeachGenie™.ai account and start generating AI-powered lesson plans, PowerPoint presentations, and quizzes in 1-2 minutes." />
      </Helmet>
      <div className="auth-wrapper">
        <div className="auth-card">
          <div className="auth-header">
            <div className="logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
              <picture>
                <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                <img src="/TechGenieMascot.png" alt="TeachGenie™.ai" style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }} />
              </picture>
              <h1>Teach<span style={{ color: '#f97316' }}>Genie</span></h1>
            </div>
            <p className="tagline">Join Educators Using AI for Better Teaching</p>

            <div className="auth-tabs">
              <Link to="/login" className="auth-tab">Login</Link>
              <Link to="/signup" className="auth-tab active">Register</Link>
            </div>
          </div>

          {error && !error.includes('fields') && !error.includes('Terms') && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fee2e2',
              borderRadius: '8px',
              marginBottom: '16px',
              color: '#991b1b',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              ⚠️ {safeError(error)}
            </div>
          )}

          {renderProgressDots()}

          <form onSubmit={handleSubmit} className="auth-form" style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Scrollable content area */}
            <div className="auth-form-container" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
              {renderStepContent()}
            </div>

            {/* Human verification for the OTP email send. Rendered on step 1 (initial
                send) and step 2 (resend). key={currentStep} forces a fresh widget —
                and thus a fresh single-use token — when moving between the two steps. */}
            {(currentStep === 1 || currentStep === 2) && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                <Turnstile key={`otp-ts-${currentStep}`} onVerify={setOtpTurnstileToken} />
              </div>
            )}

            {/* Human verification on the final step, where Create Account submits */}
            {currentStep === 4 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
                <Turnstile onVerify={setTurnstileToken} resetKey={turnstileReset} />
              </div>
            )}

            {/* Fixed buttons at bottom */}
            {currentStep > 0 && (
              <div style={{
                display: 'flex',
                gap: '8px',
                marginTop: '12px',
                width: '100%',
                boxSizing: 'border-box',
                flexShrink: 0
              }}>
                {currentStep > 0 && currentStep !== 2 && (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="btn btn-secondary"
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      minWidth: 0,
                      padding: '14px 20px',
                    }}
                  >
                    <ArrowLeft size={20} /> Back
                  </button>
                )}

                {(() => {
                  // Turnstile is only rendered (and only required) on steps
                  // 1/2 (OTP send/resend, otpTurnstileToken) and step 4
                  // (final submit, turnstileToken) — see the two <Turnstile>
                  // blocks above. Waiting for a real token before the button
                  // is clickable closes the race where a fast click submits
                  // with an empty token and the backend rejects it as
                  // "Human verification failed" — the widget just hasn't
                  // solved yet, not an actual verification failure.
                  const awaitingTurnstile = TURNSTILE_CONFIGURED && (
                    ((currentStep === 1 || currentStep === 2) && !otpTurnstileToken) ||
                    (currentStep === 4 && !turnstileToken)
                  );
                  return (
                    <button
                      type={currentStep === 4 ? 'submit' : 'button'}
                      onClick={currentStep === 4 ? undefined : nextStep}
                      disabled={loading || isSendingOtp || isVerifyingOtp || awaitingTurnstile}
                      className="btn btn-primary"
                      style={{
                        flex: currentStep < 3 ? 2 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        minWidth: 0,
                        padding: '14px 20px',
                      }}
                    >
                      {loading ? 'Creating...' :
                        isSendingOtp ? 'Sending Code...' :
                          isVerifyingOtp ? 'Verifying...' :
                            awaitingTurnstile ? 'Verifying you\'re human…' :
                              currentStep === 4 ? 'Create Account' : 'Next'}
                      {!loading && !isSendingOtp && !isVerifyingOtp && !awaitingTurnstile && currentStep < 3 && <ArrowRight size={20} />}
                    </button>
                  );
                })()}
              </div>
            )}
          </form>
        </div>

        <div className="auth-usp">
          <h2 className="usp-title" style={{ textAlign: 'center' }}>
            <span className="usp-title-navy" style={{ display: 'block', fontSize: '0.85em' }}>Revolutionize Teaching with</span>
            <span className="usp-title-navy">Teach</span>
            <span className="usp-title-gold">Genie</span>
            <span className="usp-title-gold" style={{ fontSize: '0.7em', fontWeight: '900', marginLeft: '2px', verticalAlign: 'super' }}>™</span>
            <span className="usp-title-navy">.ai</span>
          </h2>
          <h3 style={{ color: '#000000', fontSize: '1.25rem', marginTop: '0', marginBottom: '24px', fontWeight: '500', textAlign: 'center' }}>
            Your AI teaching co-instructor
          </h3>

          <div className="usp-energy-grid">
            <div className="usp-energy-card traditional">
              <h4>Traditional Workflow</h4>
              <div className="usp-energy-row">
                <span>1 Lecture</span>
                <strong>approx 12 prompts</strong>
              </div>
              <div className="usp-energy-row">
                <span>1 Prompt</span>
                <strong>0.34 kWh</strong>
              </div>
              <div className="usp-energy-row total">
                <span>Per Course</span>
                <strong>163.2 kWh</strong>
              </div>
            </div>

            <div className="usp-energy-card teachgenie">
              <div className="card-header-with-icon">
                <Leaf size={20} className="sustainability-icon leaf-3d" />
                <h4 className="workflow-title">
                  <span className="teach-text">Teach</span>
                  <span className="genie-text">Genie</span>
                  <span className="tm-text">™</span>
                  <span className="workflow-label">Workflow</span>
                </h4>
              </div>
              <div className="usp-energy-row">
                <span>1 Lecture</span>
                <strong>1 structured generation</strong>
              </div>
              <div className="usp-energy-row">
                <span>Per Lecture</span>
                <strong>0.34 kWh</strong>
              </div>
              <div className="usp-energy-row total">
                <span>Per Course</span>
                <strong>13.6 kWh</strong>
              </div>
            </div>
          </div>

          <div className="usp-impact-strip">
            <div className="impact-chip"><Gauge size={16} /> 91.7% fewer prompts</div>
            <div className="impact-chip"><Leaf size={16} /> 149.6 kWh saved per course</div>
          </div>

          <div className="usp-feature-cloud">
            <span className="usp-feature-pill">No prompt juggling</span>
            <span className="usp-feature-pill">Scenario quizzes</span>
            <span className="usp-feature-pill">Buckle Up points</span>
            <span className="usp-feature-pill">Trends and News</span>
            <span className="usp-feature-pill">Anchor slides ppt</span>
            <span className="usp-feature-pill">student handout pdf</span>
            <span className="usp-feature-pill">RBT level mapping</span>
            <span className="usp-feature-pill">In-class exercises</span>
            <span className="usp-feature-pill">Visual resources</span>
            <span className="usp-feature-pill">All in one framework</span>
          </div>
        </div>
      </div>
    </div>
  );
}